package waldo.sankan.lineamunicipalkmp.web.screens

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLCanvasElement
import waldo.sankan.lineamunicipalkmp.web.components.FilterDropdown
import waldo.sankan.lineamunicipalkmp.web.components.IncidentsTable
import kotlin.js.Date
import kotlin.js.json
import kotlin.math.roundToInt

// --- FUNCIONES DE AYUDA ---
private fun Int.percentOf(total: Int): Int {
    if (this <= 0 || total <= 0) return 0
    return (this.toDouble() / total * 100).roundToInt()
}

private fun formatDurationFromMs(ms: Double): String {
    if (ms <= 0) return "N/A"
    val totalSeconds = ms / 1000
    val totalMinutes = totalSeconds / 60
    val totalHours = totalMinutes / 60
    val days = (totalHours / 24).toInt()
    val hours = (totalHours % 24).toInt()
    val minutes = (totalMinutes % 60).toInt()
    val parts = mutableListOf<String>()
    if (days > 0) parts.add("$days d")
    if (hours > 0) parts.add("$hours h")
    if (minutes > 0 || parts.isEmpty()) parts.add("$minutes min")
    return parts.joinToString(" ")
}

@Composable
private fun StatCard(label: String, value: String, color: CSSColorValue = Color.black) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); padding(16.px) } }) {
        Span({ style { fontSize(28.px); fontWeight("bold"); color(color) } }) { Text(value) }
        Span({ style { fontSize(14.px); color(Color("#666666")) } }) { Text(label) }
    }
}

@Composable
private fun InformeGeneralFilterSection(
    uiState: AdminPanelUiState,
    onBack: () -> Unit,
    viewModel: AdminPanelViewModel
) {
    var fechaInicioStr by remember { mutableStateOf("") }
    var fechaFinStr by remember { mutableStateOf("") }
    
    val rolesDAO = listOf("DIRECCION_DAO", "ASEO", "BASURA", "BACHE", "ILUMINACION", "SEMAFORO", "AUTO_ABANDONADO")
    val rolUpper = (uiState.rolAdmin?.rol ?: "").uppercase().trim()
    val esDaoPorRol = rolUpper in rolesDAO || uiState.rolAdmin?.comunaAsignadaDisplayName?.uppercase()?.contains("DAO") == true

    Div({
        style {
            display(DisplayStyle.Flex);
            flexDirection(FlexDirection.Column);
            gap(16.px);
            padding(24.px);
            backgroundColor(Color.white);
            borderRadius(12.px);
            marginBottom(32.px);
            property("box-shadow", "0 4px 6px rgba(0,0,0,0.05)");
            boxSizing("border-box")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex);
                flexDirection(FlexDirection.Row);
                gap(16.px);
                alignItems(AlignItems.Center);
                flexWrap(FlexWrap.Wrap)
            }
        }) {
            FilterDropdown(
                label = "Período (Mes/Anual):",
                selectedValue = uiState.filtroMesEstadisticasGenerales,
                options = listOf("ANUAL") + uiState.mesesDisponibles,
                onValueChanged = { viewModel.onFiltroMesEstadisticasGeneralesChanged(it) },
                optionDisplayNames = (listOf("ANUAL" to "Resumen Anual") + uiState.mesesDisponibles.map {
                    val (year, month) = it.split("-")
                    it to Date(year.toInt(), month.toInt() - 1).toLocaleString("es-CL", options = js("{'month': 'long', 'year': 'numeric'}")).capitalize()
                }).toMap()
            )

            Span({ style { fontWeight("bold"); marginLeft(16.px) } }) { Text("Ó Rango:") }

            Input(type = InputType.Date) {
                id("fechaInicio")
                value(fechaInicioStr)
                onInput { fechaInicioStr = it.value }
                style { padding(8.px); borderRadius(4.px); border(1.px, LineStyle.Solid, Color("#cccccc")) }
            }
            Text("al")
            Input(type = InputType.Date) {
                id("fechaFin")
                value(fechaFinStr)
                onInput { fechaFinStr = it.value }
                style { padding(8.px); borderRadius(4.px); border(1.px, LineStyle.Solid, Color("#cccccc")) }
            }

            Button(attrs = {
                onClick {
                    if (fechaInicioStr.isNotBlank() && fechaFinStr.isNotBlank()) {
                        val fechaInicioDouble = Date.parse("${fechaInicioStr}T00:00:00.000Z")
                        val fechaFinDouble = Date.parse("${fechaFinStr}T23:59:59.999Z")
                        viewModel.onFiltroRangoFechasChanged(fechaInicioDouble, fechaFinDouble)
                    }
                }
                style {
                    padding(10.px, 20.px); fontSize(14.px); fontWeight("bold");
                    color(Color.white); backgroundColor(Color("#FB8C00"));
                    borderRadius(8.px); cursor("pointer")
                }
                if (fechaInicioStr.isBlank() || fechaFinStr.isBlank()) { disabled() }
            }) { Text("Filtrar") }

            Div({ style { flex(1) } })
            
            if (rolUpper == "GENERAL" || rolUpper == "COMUNAL" || esDaoPorRol) {
                if (rolUpper == "GENERAL" || rolUpper == "COMUNAL" || !esDaoPorRol) {
                    Button(attrs = {
                        onClick { viewModel.onShowInformeSeguridadClicked() }
                        style {
                            padding(12.px, 24.px); fontSize(15.px); fontWeight("bold");
                            color(Color.white); backgroundColor(Color("#303f9f"));
                            borderRadius(8.px); cursor("pointer"); marginRight(8.px)
                        }
                    }) { Text("Informe Seguridad") }
                }

                Button(attrs = {
                    onClick { viewModel.onShowEstadisticasSeguridadClicked(forzarServiciosUrbanos = true) }
                    style {
                        padding(12.px, 24.px); fontSize(15.px); fontWeight("bold");
                        color(Color.white); backgroundColor(Color("#AD1457"));
                        borderRadius(8.px); cursor("pointer"); marginRight(8.px)
                    }
                }) { Text(if (esDaoPorRol) "Informe Gestión Urbana" else "Informe Servicios Urbanos") }
            }
            
            Button(attrs = {
                onClick { onBack() }
                style {
                    padding(12.px, 24.px); fontSize(15.px); fontWeight("bold");
                    color(Color.white); backgroundColor(Color("#6c757d"));
                    borderRadius(8.px); cursor("pointer")
                }
            }) { Text("Volver al Panel") }
        }
    }
}

@Composable
fun InformeScreen(
    viewModel: AdminPanelViewModel,
    onBack: () -> Unit
) {
    val uiState by viewModel.uiState.collectAsState()
    val rolesDAO = listOf("DIRECCION_DAO", "ASEO", "BASURA", "BACHE", "ILUMINACION", "SEMAFORO", "AUTO_ABANDONADO")
    val rolUpper = (uiState.rolAdmin?.rol ?: "").uppercase().trim()
    val esDaoPorRol = rolUpper in rolesDAO || uiState.rolAdmin?.comunaAsignadaDisplayName?.uppercase()?.contains("DAO") == true
    val labelHallazgo = if (uiState.modoServiciosUrbanos || esDaoPorRol) "Hallazgo" else "Incidencia"
    val labelSolucionadas = if (uiState.modoServiciosUrbanos || esDaoPorRol) "Resueltas" else "Solucionadas"
    val labelSolucion = if (uiState.modoServiciosUrbanos || esDaoPorRol) "Resolución" else "Solución"

    Div({
        style {
            display(DisplayStyle.Flex); width(100.percent);
            backgroundColor(Color("#1976D2")); property("border-bottom", "2px solid #1565C0")
        }
    }) {
        Div({
            style {
                width(15.percent); display(DisplayStyle.Flex);
                justifyContent(JustifyContent.Center); alignItems(AlignItems.Center);
                paddingRight(16.px); paddingLeft(16.px); boxSizing("border-box")
            }
        }) {
            Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(8.px) } }) {
                Img(src = "logo_icon.svg", attrs = { style { height(120.px) } })
                H2({
                    style {
                        fontSize(24.px); color(Color.white); margin(0.px);
                        property("text-shadow", "1px 1px 2px rgba(0,0,0,0.4)")
                    }
                }) {
                    Span({ style { fontWeight("normal") } }) { Text("Linea") }
                    Span({ style { fontWeight("bold") } }) { Text("Muni") }
                }
                P(attrs = { attr("translate", "no"); style { fontSize(16.px); color(Color("#FB8C00")); fontWeight("bold"); letterSpacing(1.px); margin(0.px) } }) { Text("SMART CITY") }
            }
        }

        Div({
            style {
                width(85.percent); padding(32.px); display(DisplayStyle.Flex);
                flexDirection(FlexDirection.Column); gap(24.px); boxSizing("border-box")
            }
        }) {
            H1({
                style {
                    color(Color.white); fontSize(32.px); fontWeight(700);
                    paddingTop(30.px); margin(0.px); flex(1); textAlign("center")
                }
            }) { Text("INFORME GENERAL DE INCIDENCIAS") }

            InformeGeneralFilterSection(uiState = uiState, onBack = onBack, viewModel = viewModel)
        }
    }

    Div({
        style {
            paddingLeft(32.px); paddingRight(32.px); paddingBottom(40.px);
            backgroundColor(Color("#1976D2")); display(DisplayStyle.Flex);
            flexDirection(FlexDirection.Column); gap(32.px)
        }
    }) {
        if (uiState.isLoading) {
            P({ style { color(Color.white) } }) { Text("Calculando informe...") }
        } else if (uiState.estadisticas.isEmpty()) {
            Div({
                style {
                    padding(24.px); textAlign("center");
                    backgroundColor(Color.white); borderRadius(12.px)
                }
            }) { P { Text("No hay datos de estadísticas para el período seleccionado.") } }
        } else {
            val statsMes = uiState.estadisticas.first()
            val resumen = statsMes.resumenGeneral
            val todosLosTipos = statsMes.desglosePorTipo.sortedByDescending { it.resumen.total }

            // --- TARJETA DE RESUMEN SUPERIOR ---
            Div({
                style {
                    backgroundColor(Color.white); borderRadius(12.px);
                    padding(24.px); property("box-shadow", "0px 4px 15px rgba(0,0,0,0.08)")
                }
            }) {
                H2({
                    style {
                        fontSize(24.px); color(Color("#1976D2")); marginTop(0.px);
                        marginBottom(24.px); property("border-bottom", "1px solid #e0e0e0");
                        paddingBottom(16.px)
                    }
                }) {
                    val tituloPeriodo = if (uiState.filtroMesEstadisticasGenerales == "ANUAL") "RESUMEN ANUAL" else statsMes.mes
                    Text(tituloPeriodo)
                }
                Div({
                    style {
                        display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceAround);
                        alignItems(AlignItems.Center); padding(16.px);
                        backgroundColor(Color("#fafafa")); borderRadius(8.px)
                    }
                }) {
                    StatCard(label = "Total ${labelHallazgo}s", value = resumen.total.toString())
                    StatCard(label = labelSolucionadas, value = resumen.solucionadas.toString(), color = Color("#4CAF50"))
                    StatCard(label = "En Proceso", value = resumen.enProceso.toString(), color = Color("#FFC107"))
                    StatCard(label = "Rechazadas", value = resumen.rechazadas.toString(), color = Color("#F44336"))
                    StatCard(label = "Verificadas", value = resumen.verificadas.toString(), color = Color("#3F51B5"))
                    StatCard(label = "T° Prom. $labelSolucion", value = formatDurationFromMs(resumen.tiempoPromedioSolucionMs))
                }
            }

            // --- FILA 1: ANÁLISIS DE CANTIDADES (40/60) ---
            Div({
                style {
                    backgroundColor(Color.white); borderRadius(12.px);
                    padding(24.px); property("box-shadow", "0px 4px 15px rgba(0,0,0,0.08)")
                }
            }) {
                H2({ style { fontSize(22.px); color(Color("#1976D2")); marginTop(0.px); marginBottom(24.px) } }) { Text("Resumen por Tipo de $labelHallazgo") }
                Div({ style { display(DisplayStyle.Flex); gap(24.px); alignItems(AlignItems.Start) } }) {
                    // Tabla (40%)
                    Div({ style { flexBasis(40.percent) } }) {
                        Table({ style { width(100.percent); property("border-collapse", "collapse"); fontSize(14.px) } }) {
                            Thead({ style { backgroundColor(Color("#eef2f5")) } }) {
                                Tr {
                                    Th({ style { padding(12.px); textAlign("left") } }) { Text("Tipo de $labelHallazgo") }
                                    Th({ style { padding(12.px) } }) { Text("Total") }
                                    Th({ style { padding(12.px); color(Color("#4CAF50")) } }) { Text(labelSolucionadas) }
                                    Th({ style { padding(12.px); color(Color("#FFC107")) } }) { Text("En Proceso") }
                                    Th({ style { padding(12.px); color(Color("#F44336")) } }) { Text("Rechazadas") }
                                    Th({ style { padding(12.px); color(Color("#3F51B5")) } }) { Text("Verificadas") }
                                }
                            }
                            Tbody {
                                todosLosTipos.forEach { statsTipo ->
                                    Tr({ style { property("border-bottom", "1px solid #e0e0e0") } }) {
                                        Td({ style { padding(12.px); fontWeight("bold") } }) { Text(statsTipo.tipoIncidencia) }
                                        Td({ style { padding(12.px); textAlign("center") } }) { Text(statsTipo.resumen.total.toString()) }
                                        Td({ style { padding(12.px); textAlign("center") } }) { Text(statsTipo.resumen.solucionadas.toString()) }
                                        Td({ style { padding(12.px); textAlign("center") } }) { Text(statsTipo.resumen.enProceso.toString()) }
                                        Td({ style { padding(12.px); textAlign("center") } }) { Text(statsTipo.resumen.rechazadas.toString()) }
                                        Td({ style { padding(12.px); textAlign("center") } }) { Text(statsTipo.resumen.verificadas.toString()) }
                                    }
                                }
                            }
                        }
                    }
                    // Gráficos (60%)
                    Div({ style { flexBasis(60.percent); display(DisplayStyle.Flex); gap(16.px) } }) {
                        Div({ style { flex(1); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(16.px) } }) {
                            H3({ style { fontSize(18.px); color(Color("#333333")); margin(0.px) } }) { Text("Distribución de Estados") }
                            Div({ style { width(100.percent); height(300.px); position(Position.Relative) } }) {
                                Canvas(attrs = { id("pieChart-informe-estados") })
                                DisposableEffect(resumen) {
                                    val canvas = document.getElementById("pieChart-informe-estados") as? HTMLCanvasElement
                                    var chart: Chart? = null
                                    if (canvas != null) {
                                        val data = json(
                                            "labels" to arrayOf(labelSolucionadas, "En Proceso", "Rechazadas", "Verificadas"),
                                            "datasets" to arrayOf(json(
                                                "data" to arrayOf(resumen.solucionadas, resumen.enProceso, resumen.rechazadas, resumen.verificadas),
                                                "backgroundColor" to arrayOf("#4CAF50", "#FFC107", "#F44336", "#3F51B5")
                                            ))
                                        )
                                        chart = Chart(canvas.getContext("2d"), json("type" to "pie", "data" to data, "options" to json("responsive" to true, "maintainAspectRatio" to false)))
                                    }
                                    onDispose { chart?.destroy() }
                                }
                            }
                        }
                        Div({ style { flex(1); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(16.px) } }) {
                            H3({ style { fontSize(18.px); color(Color("#333333")); margin(0.px) } }) { Text("Composición por Tipo") }
                            Div({ style { width(100.percent); height(300.px); position(Position.Relative) } }) {
                                Canvas(attrs = { id("barChart-informe-composicion") })
                                DisposableEffect(todosLosTipos) {
                                    val canvas = document.getElementById("barChart-informe-composicion") as? HTMLCanvasElement
                                    var chart: Chart? = null
                                    if (canvas != null) {
                                        val data = json(
                                            "labels" to todosLosTipos.map { it.tipoIncidencia }.toTypedArray(),
                                            "datasets" to arrayOf(
                                                json("label" to labelSolucionadas, "data" to todosLosTipos.map { it.resumen.solucionadas }.toTypedArray(), "backgroundColor" to "#4CAF50"),
                                                json("label" to "En Proceso", "data" to todosLosTipos.map { it.resumen.enProceso }.toTypedArray(), "backgroundColor" to "#FFC107"),
                                                json("label" to "Rechazadas", "data" to todosLosTipos.map { it.resumen.rechazadas }.toTypedArray(), "backgroundColor" to "#F44336"),
                                                json("label" to "Verificadas", "data" to todosLosTipos.map { it.resumen.verificadas }.toTypedArray(), "backgroundColor" to "#3F51B5")
                                            )
                                        )
                                        chart = Chart(canvas.getContext("2d"), json("type" to "bar", "data" to data, "options" to json("responsive" to true, "maintainAspectRatio" to false, "scales" to json("x" to json("stacked" to true), "y" to json("stacked" to true)))))
                                    }
                                    onDispose { chart?.destroy() }
                                }
                            }
                        }
                    }
                }
            }

            // --- FILA 2: ANÁLISIS DE PORCENTAJES (40/60) ---
            Div({
                style {
                    backgroundColor(Color.white); borderRadius(12.px);
                    padding(24.px); property("box-shadow", "0px 4px 15px rgba(0,0,0,0.08)")
                }
            }) {
                H2({ style { fontSize(22.px); color(Color("#1976D2")); marginTop(0.px); marginBottom(24.px) } }) { Text("Análisis por Porcentaje") }
                Div({ style { display(DisplayStyle.Flex); gap(24.px); alignItems(AlignItems.Start) } }) {
                    Div({ style { flexBasis(40.percent) } }) {
                        Table({ style { width(100.percent); property("border-collapse", "collapse"); fontSize(14.px) } }) {
                            Thead({ style { backgroundColor(Color("#eef2f5")) } }) {
                                Tr {
                                    Th({ style { padding(12.px); textAlign("left") } }) { Text("Tipo de $labelHallazgo") }
                                    Th({ style { padding(12.px) } }) { Text("% del Total") }
                                    Th({ style { padding(12.px) } }) { Text("% $labelSolucionadas") }
                                    Th({ style { padding(12.px) } }) { Text("% En Proceso") }
                                    Th({ style { padding(12.px) } }) { Text("% Rechazadas") }
                                    Th({ style { padding(12.px) } }) { Text("% Verificadas") }
                                }
                            }
                            Tbody {
                                val totalG = resumen.total
                                todosLosTipos.forEach { statsTipo ->
                                    Tr({ style { property("border-bottom", "1px solid #e0e0e0") } }) {
                                        Td({ style { padding(12.px); fontWeight("bold") } }) { Text(statsTipo.tipoIncidencia) }
                                        Td({ style { padding(12.px); textAlign("center") } }) { Text("${statsTipo.resumen.total.percentOf(totalG)}%") }
                                        Td({ style { padding(12.px); textAlign("center") } }) { Text("${statsTipo.resumen.solucionadas.percentOf(statsTipo.resumen.total)}%") }
                                        Td({ style { padding(12.px); textAlign("center") } }) { Text("${statsTipo.resumen.enProceso.percentOf(statsTipo.resumen.total)}%") }
                                        Td({ style { padding(12.px); textAlign("center") } }) { Text("${statsTipo.resumen.rechazadas.percentOf(statsTipo.resumen.total)}%") }
                                        Td({ style { padding(12.px); textAlign("center") } }) { Text("${statsTipo.resumen.verificadas.percentOf(statsTipo.resumen.total)}%") }
                                    }
                                }
                            }
                        }
                    }
                    Div({ style { flexBasis(60.percent); display(DisplayStyle.Flex); gap(24.px) } }) {
                        Div({ style { flex(1); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(16.px) } }) {
                            H3({ style { fontSize(18.px); color(Color("#333333")); margin(0.px) } }) { Text("Distribución por Tipo (%)") }
                            Div({ style { width(100.percent); height(300.px); position(Position.Relative) } }) {
                                Canvas(attrs = { id("pieChart-informe-porcentajes") })
                                DisposableEffect(todosLosTipos) {
                                    val canvas = document.getElementById("pieChart-informe-porcentajes") as? HTMLCanvasElement
                                    var chart: Chart? = null
                                    if (canvas != null) {
                                        val labels = todosLosTipos.map { it.tipoIncidencia }.toTypedArray()
                                        val data = json(
                                            "labels" to labels,
                                            "datasets" to arrayOf(json(
                                                "data" to todosLosTipos.map { it.resumen.total.percentOf(resumen.total) }.toTypedArray(),
                                                "backgroundColor" to arrayOf("#4CAF50", "#FFC107", "#F44336", "#3F51B5", "#9C27B0", "#03A9F4", "#E91E63", "#795548")
                                            ))
                                        )
                                        chart = Chart(canvas.getContext("2d"), json("type" to "pie", "data" to data, "options" to json("responsive" to true, "maintainAspectRatio" to false)))
                                    }
                                    onDispose { chart?.destroy() }
                                }
                            }
                        }
                        Div({ style { flex(1); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); alignItems(AlignItems.Center); gap(16.px) } }) {
                            H3({ style { fontSize(18.px); color(Color("#333333")); margin(0.px) } }) { Text("Cumplimiento por Tipo (%)") }
                            Div({ style { width(100.percent); height(300.px); position(Position.Relative) } }) {
                                Canvas(attrs = { id("barChart-informe-cumplimiento") })
                                DisposableEffect(todosLosTipos) {
                                    val canvas = document.getElementById("barChart-informe-cumplimiento") as? HTMLCanvasElement
                                    var chart: Chart? = null
                                    if (canvas != null) {
                                        val data = json(
                                            "labels" to todosLosTipos.map { it.tipoIncidencia }.toTypedArray(),
                                            "datasets" to arrayOf(
                                                json("label" to "% $labelSolucionadas", "data" to todosLosTipos.map { it.resumen.solucionadas.percentOf(it.resumen.total) }.toTypedArray(), "backgroundColor" to "#4CAF50"),
                                                json("label" to "% En Proceso", "data" to todosLosTipos.map { it.resumen.enProceso.percentOf(it.resumen.total) }.toTypedArray(), "backgroundColor" to "#FFC107"),
                                                json("label" to "% Rechazadas", "data" to todosLosTipos.map { it.resumen.rechazadas.percentOf(it.resumen.total) }.toTypedArray(), "backgroundColor" to "#F44336"),
                                                json("label" to "% Verificadas", "data" to todosLosTipos.map { it.resumen.verificadas.percentOf(it.resumen.total) }.toTypedArray(), "backgroundColor" to "#3F51B5")
                                            )
                                        )
                                        chart = Chart(canvas.getContext("2d"), json("type" to "bar", "data" to data, "options" to json("responsive" to true, "maintainAspectRatio" to false, "scales" to json("x" to json("stacked" to true), "y" to json("stacked" to true, "ticks" to json("callback" to js("function(value) { return value + '%'; }")))))))
                                    }
                                    onDispose { chart?.destroy() }
                                }
                            }
                        }
                    }
                }
            }

            // --- DETALLE FINAL DE INCIDENCIAS (TABLA COMPLETA) ---
            Div({
                style {
                    backgroundColor(Color.white); borderRadius(12.px);
                    padding(24.px); property("box-shadow", "0px 4px 15px rgba(0,0,0,0.08)")
                }
            }) {
                H2({ style { fontSize(22.px); color(Color("#1976D2")); marginTop(0.px); marginBottom(24.px) } }) {
                    Text("Detalle de ${labelHallazgo}s del Período")
                }
                Div({ style { overflowX("auto") } }) {
                    IncidentsTable(
                        incidencias = uiState.incidencias,
                        onIncidenciaClick = { viewModel.onIncidenciaSelected(it) }
                    )
                }
            }
        }
    }
}
